/**
 * InfrastructureInspector — Slide-up panel for selected infrastructure object.
 *
 * Shows: template context, measurements, attributes, entity owner,
 * linked API endpoints and IoT devices.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Edit3, Save, Trash2, Link2,
  Building2, Cpu, Check, Sparkles, Unlink, Search, Plus,
  RefreshCw, FileText, PenTool,
} from 'lucide-react';
import { useMapStore } from '../MapContext';
import {
  useInfrastructure,
  useUpdateInfrastructure,
  useDeleteInfrastructure,
  useUnlinkInfraDevice,
  useInfraTwinState,
  useLinkInfraDevice,
  useIoTDevices,
  useLinkInfrastructureEntity,
  useSyncInfrastructure,
  useWorkspaceApis,
  useApiEndpoints,
  useLinkInfrastructureApi,
  useUnlinkInfrastructureApi,
  useInfrastructureTaxonomy,
} from '../api';
import {
  ALL_CATEGORIES,
  FEATURE_STATUS_COLORS,
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  CATEGORY_LABELS,
  buildInfraIkuType,
  buildInfraTemplateId,
} from '../types';
import type { FeatureStatus, InfraCategoryType, LinkedApiEndpoint, MapInfrastructure, WorkspaceApi } from '../types';
import { toast } from 'sonner';
import { usePartyEntities } from '@/features/entity/api';

const ENTITY_REMINDER_EVENT = 'mapper:entity-reminder';

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

const INFRA_STATUS_OPTIONS: FeatureStatus[] = ['active', 'draft', 'review', 'decommissioned'];

function AnimatedNumber({ value, suffix = '', decimals = 1 }: { value: number; suffix?: string; decimals?: number }) {
  return (
    <motion.span key={value.toFixed(decimals)} initial={{ opacity: 0.5, y: 2 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {value.toFixed(decimals)}{suffix}
    </motion.span>
  );
}

function toPrettyJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

function getInfraTypeLeaf(infraType: string | null | undefined): string {
  if (!infraType) return '';
  const parts = String(infraType).split(':');
  return parts[parts.length - 1] || String(infraType);
}

function canEditGeometryVisually(geometry: GeoJSON.Geometry | null | undefined): boolean {
  return !!geometry && (
    geometry.type === 'Point' ||
    geometry.type === 'LineString' ||
    geometry.type === 'Polygon'
  );
}

/* ------------------------------------------------------------------ */
/*  Inline Pickers                                                      */
/* ------------------------------------------------------------------ */

function DeviceSearchPicker({
  linkedIds,
  onLink,
  onClose,
}: {
  linkedIds: string[];
  onLink: (deviceId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: devices } = useIoTDevices({ q: query, limit: 20 });
  const available = (devices ?? []).filter((d) => !linkedIds.includes(d.deviceId));

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="overflow-hidden mb-2"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100">
          <Search className="w-3 h-3 text-gray-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search devices..."
            className="text-xs bg-transparent outline-none flex-1"
          />
          <button onClick={onClose} className="p-0.5 text-gray-300 hover:text-gray-500">
            <X className="w-3 h-3" />
          </button>
        </div>
        <div className="max-h-28 overflow-y-auto">
          {available.map((device) => {
            const isOnline = device.status === 'online' || device.status === 'active';
            return (
              <button
                key={device.deviceId}
                onClick={() => onLink(device.deviceId)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 transition-colors"
              >
                <Cpu className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="truncate flex-1 text-gray-700 font-mono">{device.deviceId}</span>
                <span className="text-[10px] text-gray-400">{device.type}</span>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
              </button>
            );
          })}
          {available.length === 0 && (
            <p className="text-xs text-gray-300 text-center py-2">No available devices</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function EntitySearchPicker({
  onLink,
  onClose,
}: {
  onLink: (entityId: string, entityName: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data } = usePartyEntities({ limit: 20, search: query });
  const entities = data?.entities ?? [];

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="overflow-hidden mt-1.5"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100">
          <Search className="w-3 h-3 text-gray-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search entities..."
            className="text-xs bg-transparent outline-none flex-1"
          />
          <button onClick={onClose} className="p-0.5 text-gray-300 hover:text-gray-500">
            <X className="w-3 h-3" />
          </button>
        </div>
        <div className="max-h-32 overflow-y-auto">
          {entities.map((entity) => (
            <button
              key={entity.id}
              onClick={() => onLink(entity.id, entity.name)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 transition-colors"
            >
              <Building2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="truncate flex-1 text-gray-700">{entity.name}</span>
              <span className="text-[10px] text-gray-400">{entity.entity_type?.replace('legal:', '')}</span>
            </button>
          ))}
          {entities.length === 0 && (
            <p className="text-xs text-gray-300 text-center py-2">No matching entities</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ApiEndpointSearchPicker({
  linkedEndpoints,
  onLink,
  onClose,
}: {
  linkedEndpoints: LinkedApiEndpoint[];
  onLink: (link: LinkedApiEndpoint) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedApi, setSelectedApi] = useState<WorkspaceApi | null>(null);
  const { data: apis } = useWorkspaceApis({ search: query, limit: 25 });
  const { data: endpoints } = useApiEndpoints(selectedApi?.api_id);

  const availableEndpoints = (endpoints ?? []).filter((endpoint) => (
    !linkedEndpoints.some((link) => link.api_id === selectedApi?.api_id && link.endpoint_id === endpoint.endpoint_id)
  ));

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="overflow-hidden mb-2"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100">
          <Search className="w-3 h-3 text-gray-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={selectedApi ? 'Filter APIs...' : 'Search APIs...'}
            className="text-xs bg-transparent outline-none flex-1"
          />
          <button onClick={onClose} className="p-0.5 text-gray-300 hover:text-gray-500">
            <X className="w-3 h-3" />
          </button>
        </div>
        {!selectedApi ? (
          <div className="max-h-36 overflow-y-auto">
            {(apis ?? []).map((api) => (
              <button
                key={api.api_id}
                onClick={() => setSelectedApi(api)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 transition-colors"
              >
                <Link2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-gray-700 truncate">{api.api_name || api.api_id}</div>
                  <div className="text-[10px] text-gray-400 truncate">
                    {api.provider || 'API'} {api.runtime?.enabled_pipeline_count != null ? `· ${api.runtime.enabled_pipeline_count} live pipelines` : ''}
                  </div>
                </div>
              </button>
            ))}
            {(apis ?? []).length === 0 && (
              <p className="text-xs text-gray-300 text-center py-2">No APIs found</p>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-100 bg-gray-50">
              <button
                onClick={() => setSelectedApi(null)}
                className="text-[10px] font-medium text-gray-500 hover:text-gray-700"
              >
                Back
              </button>
              <span className="text-[10px] font-medium text-gray-600 truncate">{selectedApi.api_name || selectedApi.api_id}</span>
            </div>
            <div className="max-h-36 overflow-y-auto">
              {availableEndpoints.map((endpoint) => (
                <button
                  key={endpoint.endpoint_id}
                  onClick={() => onLink({
                    api_id: selectedApi.api_id,
                    api_name: selectedApi.api_name || selectedApi.api_id,
                    provider: selectedApi.provider || null,
                    endpoint_id: endpoint.endpoint_id,
                    method: endpoint.method || null,
                    path: endpoint.path || null,
                    template_id: endpoint.template_id || null,
                  })}
                  className="w-full flex items-start gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                >
                  <Link2 className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-gray-700 truncate font-medium">{endpoint.endpoint_id}</div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {(endpoint.method || 'GET').toUpperCase()} {endpoint.path || '/'}
                    </div>
                  </div>
                </button>
              ))}
              {availableEndpoints.length === 0 && (
                <p className="text-xs text-gray-300 text-center py-2">No unlinked endpoints for this API</p>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */

export default function InfrastructureInspector() {
  const {
    selectedInfrastructure,
    setSelectedInfrastructure,
    inspectorOpen,
    setInspectorOpen,
    setDevicePaletteOpen,
    editingInfrastructureGeometryId,
    editingInfrastructureGeometryDraft,
    startEditingInfrastructureGeometry,
    clearEditingInfrastructureGeometry,
  } = useMapStore();
  const { data: latestInfrastructure } = useInfrastructure(selectedInfrastructure?.id);
  const updateInfra = useUpdateInfrastructure();
  const deleteInfra = useDeleteInfrastructure();
  const unlinkDevice = useUnlinkInfraDevice();
  const linkDevice = useLinkInfraDevice();
  const linkEntity = useLinkInfrastructureEntity();
  const syncInfra = useSyncInfrastructure();
  const linkApi = useLinkInfrastructureApi();
  const unlinkApi = useUnlinkInfrastructureApi();
  const { data: twinState } = useInfraTwinState(selectedInfrastructure?.id);
  const { data: entityData } = usePartyEntities({ limit: 50 });
  const { data: taxonomy } = useInfrastructureTaxonomy();

  const infra = latestInfrastructure || selectedInfrastructure;
  const entities = entityData?.entities ?? [];

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState<InfraCategoryType>('buildings');
  const [editSubcategory, setEditSubcategory] = useState('');
  const [editTypeValue, setEditTypeValue] = useState('');
  const [editStatus, setEditStatus] = useState<FeatureStatus>('active');
  const [editParcelId, setEditParcelId] = useState('');
  const [editLayerId, setEditLayerId] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');
  const [editIndustryTags, setEditIndustryTags] = useState('');
  const [editAttributes, setEditAttributes] = useState('{}');
  const [editGeometry, setEditGeometry] = useState('{}');
  const [deviceSearchOpen, setDeviceSearchOpen] = useState(false);
  const [entitySearchOpen, setEntitySearchOpen] = useState(false);
  const [apiSearchOpen, setApiSearchOpen] = useState(false);

  const resetEditForm = (item: MapInfrastructure) => {
    const nextCategory = ALL_CATEGORIES.includes(item.category as InfraCategoryType)
      ? item.category as InfraCategoryType
      : 'buildings';
    setEditName(item.name || '');
    setEditCategory(nextCategory);
    setEditSubcategory(item.subcategory || '');
    setEditTypeValue(item.infra_type || '');
    setEditStatus(item.status);
    setEditParcelId(item.parcel_id || '');
    setEditLayerId(item.layer_id || '');
    setEditTemplateId(item.template_id || '');
    setEditIndustryTags((item.industry_tags || []).join(', '));
    setEditAttributes(toPrettyJson(item.attributes || {}));
    setEditGeometry(toPrettyJson(item.geometry || {}));
  };

  useEffect(() => {
    if (!infra || editing) return;
    resetEditForm(infra);
  }, [infra, editing]);

  const editCategoryDef = taxonomy?.categories?.[editCategory];
  const editSubcategories = Object.keys(editCategoryDef?.subcategories || {});
  const editTypes = editSubcategory ? (editCategoryDef?.subcategories?.[editSubcategory] || []) : [];

  useEffect(() => {
    if (!editing) return;
    if (!editSubcategories.length) {
      if (editSubcategory) setEditSubcategory('');
      return;
    }
    if (!editSubcategory || !editSubcategories.includes(editSubcategory)) {
      setEditSubcategory(editSubcategories[0]);
    }
  }, [editing, editCategory, editSubcategory, editSubcategories]);

  useEffect(() => {
    if (!editing || !editTypes.length) return;
    if (editTypeValue && !editTypeValue.startsWith('iku:infrastructure:')) return;
    const currentLeaf = getInfraTypeLeaf(editTypeValue);
    const nextLeaf = editTypes.includes(currentLeaf) ? currentLeaf : editTypes[0];
    const nextType = buildInfraIkuType(editCategory, editSubcategory, nextLeaf);
    if (nextType !== editTypeValue) {
      setEditTypeValue(nextType);
    }
  }, [editing, editCategory, editSubcategory, editTypeValue, editTypes]);

  if (!infra || !inspectorOpen) return null;

  const statusColor = FEATURE_STATUS_COLORS[infra.status] || FEATURE_STATUS_COLORS.active;
  const categoryColor = CATEGORY_COLORS[infra.category as InfraCategoryType] || CATEGORY_COLORS.buildings;
  const CategoryIcon = CATEGORY_ICONS[infra.category as InfraCategoryType] || Building2;
  const categoryLabel = CATEGORY_LABELS[infra.category as InfraCategoryType] || infra.category;
  const areaHa = infra.area_m2 / 10000;
  const perimeterKm = infra.perimeter_m / 1000;
  const typeLabel = infra.infra_type?.split(':').pop()?.replace(/_/g, ' ') || infra.infra_type;
  const linkedEntity = twinState?.entity || entities.find((entity) => entity.id === infra.linked_entity_id) || null;
  const apiLinks = twinState?.api_links ?? infra.linked_api_endpoints ?? [];
  const streamLinks = twinState?.streams ?? [];
  const isGeometryEditing = editingInfrastructureGeometryId === infra.id;
  const supportsVisualGeometryEdit = canEditGeometryVisually(infra.geometry);

  const handleStartEditing = () => {
    resetEditForm(infra);
    setEditing(true);
  };

  const handleCancelEditing = () => {
    resetEditForm(infra);
    setEditing(false);
  };

  const handleSaveDetails = () => {
    const trimmedName = editName.trim();
    const trimmedType = editTypeValue.trim();

    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!trimmedType) {
      toast.error('Infrastructure type is required');
      return;
    }

    let parsedAttributes: Record<string, unknown>;
    let parsedGeometry: GeoJSON.Geometry;

    try {
      const nextAttributes = JSON.parse(editAttributes || '{}');
      if (!nextAttributes || Array.isArray(nextAttributes) || typeof nextAttributes !== 'object') {
        throw new Error('Attributes must be a JSON object');
      }
      parsedAttributes = nextAttributes as Record<string, unknown>;
    } catch (err) {
      toast.error('Invalid attributes JSON', { description: String(err) });
      return;
    }

    try {
      const nextGeometry = JSON.parse(editGeometry || '{}');
      if (!nextGeometry || typeof nextGeometry !== 'object' || typeof nextGeometry.type !== 'string' || nextGeometry.coordinates === undefined) {
        throw new Error('Geometry must be valid GeoJSON');
      }
      parsedGeometry = nextGeometry as GeoJSON.Geometry;
    } catch (err) {
      toast.error('Invalid geometry JSON', { description: String(err) });
      return;
    }

    updateInfra.mutate(
      {
        id: infra.id,
        name: trimmedName,
        category: editCategory,
        subcategory: editSubcategory || '',
        infra_type: trimmedType,
        industry_tags: editIndustryTags.split(',').map((tag) => tag.trim()).filter(Boolean),
        attributes: parsedAttributes,
        geometry: parsedGeometry,
        parcel_id: editParcelId.trim() || null,
        layer_id: editLayerId.trim() || null,
        status: editStatus,
        template_id: editTemplateId.trim() || buildInfraTemplateId(editCategory, editSubcategory || 'general', getInfraTypeLeaf(trimmedType)),
      },
      {
        onSuccess: (updated) => {
          setSelectedInfrastructure(updated);
          setEditing(false);
          toast.success('Infrastructure updated');
        },
        onError: (err) => toast.error('Update failed', { description: String(err) }),
      }
    );
  };

  const handleStartGeometryEdit = () => {
    if (!supportsVisualGeometryEdit) {
      toast.error('Visual geometry editing currently supports points, lines, and polygons only.');
      return;
    }
    startEditingInfrastructureGeometry(infra.id, infra.geometry);
    toast.info('Geometry edit mode active', {
      description: 'Drag the shape directly on the map, then save or cancel from the inspector.',
    });
  };

  const handleCancelGeometryEdit = () => {
    clearEditingInfrastructureGeometry();
    toast.info('Geometry edit cancelled');
  };

  const handleSaveGeometryEdit = () => {
    if (!editingInfrastructureGeometryDraft) {
      toast.error('No edited geometry to save');
      return;
    }

    updateInfra.mutate(
      {
        id: infra.id,
        geometry: editingInfrastructureGeometryDraft,
      },
      {
        onSuccess: (updated) => {
          setSelectedInfrastructure(updated);
          clearEditingInfrastructureGeometry();
          toast.success('Geometry updated');
        },
        onError: (err) => toast.error('Geometry update failed', { description: String(err) }),
      }
    );
  };

  const handleDelete = () => {
    if (confirm(`Delete infrastructure "${infra.name}"?`)) {
      deleteInfra.mutate(infra.id, {
        onSuccess: () => {
          clearEditingInfrastructureGeometry();
          setSelectedInfrastructure(null);
          setInspectorOpen(false);
          toast.success('Infrastructure deleted');
        },
        onError: (err) => toast.error('Delete failed', { description: String(err) }),
      });
    }
  };

  const handleConfirmReview = () => {
    updateInfra.mutate({ id: infra.id, status: 'active' }, {
      onSuccess: () => toast.success('Infrastructure confirmed'),
      onError: (err) => toast.error('Confirm failed', { description: String(err) }),
    });
  };

  const handleUnlinkDevice = (deviceId: string) => {
    unlinkDevice.mutate(
      { infraId: infra.id, device_id: deviceId },
      {
        onSuccess: () => toast.info(`Device ${deviceId} unlinked`),
        onError: (err) => toast.error('Unlink failed', { description: String(err) }),
      }
    );
  };

  const handleSync = () => {
    if (!linkedEntity && !infra.linked_entity_id) {
      window.dispatchEvent(new CustomEvent(ENTITY_REMINDER_EVENT, {
        detail: {
          message: 'Link a legal entity before syncing this object layer into assets or PIM.',
        },
      }));
      setEntitySearchOpen(true);
      toast.info('Link a legal entity before syncing this object layer.');
      return;
    }
    syncInfra.mutate(infra.id, {
      onSuccess: (result) => {
        const assetId = result?.asset_id || result?.product_id || null;
        toast.success(
          assetId
            ? 'Infrastructure synced and asset-backed record updated'
            : 'Infrastructure synced to Contextualizer and PIM'
        );
      },
      onError: (err) => toast.error('Sync failed', { description: String(err) }),
    });
  };

  const handleClearOwner = () => {
    updateInfra.mutate(
      { id: infra.id, linked_entity_id: null },
      {
        onSuccess: () => toast.success('Owner link cleared'),
        onError: (err) => toast.error('Failed to clear owner', { description: String(err) }),
      }
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 200, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 200, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="border-t border-gray-200 bg-white/95 backdrop-blur-md max-h-[55vh] overflow-y-auto"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-md z-10">
          <div className={`w-8 h-8 rounded-lg ${categoryColor.bg} flex items-center justify-center`}>
            <CategoryIcon className={`w-4 h-4 ${categoryColor.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="text-sm font-medium text-gray-900 bg-transparent border-b border-blue-400 outline-none w-full"
                />
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-gray-900 truncate">{infra.name}</span>
                <button
                  onClick={handleStartEditing}
                  className="p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColor.bg} ${statusColor.text}`}>
                {infra.status}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${categoryColor.bg} ${categoryColor.text}`}>
                {categoryLabel}
              </span>
              {infra.subcategory && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  {infra.subcategory.replace(/_/g, ' ')}
                </span>
              )}
              <span className="text-[10px] text-gray-400 capitalize">{typeLabel}</span>
              {infra.source?.startsWith('agent') && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center gap-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> AI
                </span>
              )}
            </div>
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => { setSelectedInfrastructure(null); setInspectorOpen(false); }}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </div>

        <div className="px-4 py-3 grid grid-cols-2 gap-3">
          {infra.area_m2 > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Area</div>
              <div className="text-sm font-semibold text-gray-800"><AnimatedNumber value={areaHa} suffix=" ha" /></div>
              <div className="text-[10px] text-gray-400"><AnimatedNumber value={infra.area_m2} suffix=" m\u00B2" decimals={0} /></div>
            </div>
          )}
          {infra.perimeter_m > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Perimeter</div>
              <div className="text-sm font-semibold text-gray-800"><AnimatedNumber value={perimeterKm} suffix=" km" decimals={2} /></div>
              <div className="text-[10px] text-gray-400"><AnimatedNumber value={infra.perimeter_m} suffix=" m" decimals={0} /></div>
            </div>
          )}
        </div>

        {!editing && isGeometryEditing && (
          <div className="px-4 py-2 border-t border-blue-100 bg-blue-50/80">
            <div className="text-xs font-medium text-blue-800">Geometry edit mode is active</div>
            <div className="text-[10px] text-blue-700/80">
              Drag vertices or move the feature on the map, then save or cancel here.
            </div>
          </div>
        )}

        {editing && (
          <div className="px-4 py-3 border-t border-gray-100 space-y-3">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Edit Details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Category</div>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value as InfraCategoryType)}
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                >
                  {ALL_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Status</div>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as FeatureStatus)}
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                >
                  {INFRA_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Subcategory</div>
                {editSubcategories.length > 0 ? (
                  <select
                    value={editSubcategory}
                    onChange={(e) => setEditSubcategory(e.target.value)}
                    className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                  >
                    {editSubcategories.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>
                        {subcategory.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={editSubcategory}
                    onChange={(e) => setEditSubcategory(e.target.value)}
                    placeholder="e.g. boundaries"
                    className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                  />
                )}
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Suggested Type</div>
                <select
                  value={editTypes.includes(getInfraTypeLeaf(editTypeValue)) ? getInfraTypeLeaf(editTypeValue) : ''}
                  onChange={(e) => {
                    const nextLeaf = e.target.value;
                    if (!nextLeaf) return;
                    setEditTypeValue(buildInfraIkuType(editCategory, editSubcategory, nextLeaf));
                  }}
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                >
                  <option value="">Custom / keep current</option>
                  {editTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Type / IKU</div>
              <input
                value={editTypeValue}
                onChange={(e) => setEditTypeValue(e.target.value)}
                placeholder="iku:infrastructure:linear:boundaries:fence_boundary"
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Parcel ID</div>
                <input
                  value={editParcelId}
                  onChange={(e) => setEditParcelId(e.target.value)}
                  placeholder="Optional parcel id"
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono"
                />
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Layer ID</div>
                <input
                  value={editLayerId}
                  onChange={(e) => setEditLayerId(e.target.value)}
                  placeholder="Optional layer id"
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono"
                />
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Template ID</div>
                <input
                  value={editTemplateId}
                  onChange={(e) => setEditTemplateId(e.target.value)}
                  placeholder="Optional template id"
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono"
                />
              </div>
              <div>
                <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Industry Tags</div>
                <input
                  value={editIndustryTags}
                  onChange={(e) => setEditIndustryTags(e.target.value)}
                  placeholder="agriculture, common"
                  className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Attributes JSON</div>
              <textarea
                value={editAttributes}
                onChange={(e) => setEditAttributes(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono resize-y"
              />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1">Geometry GeoJSON</div>
              <textarea
                value={editGeometry}
                onChange={(e) => setEditGeometry(e.target.value)}
                rows={8}
                className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-blue-400 font-mono resize-y"
              />
            </div>
          </div>
        )}

        {!editing && infra.template_id && (
          <div className="px-4 py-2 border-t border-gray-100">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Contextualizer Template</div>
            <div className="flex items-start gap-2 text-xs bg-amber-50 rounded-md px-2 py-1.5 border border-amber-100">
              <FileText className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              <span className="text-amber-800 font-mono break-all">{infra.template_id}</span>
            </div>
          </div>
        )}

        {!editing && (infra.land_category || infra.asset_mapping || twinState?.operational_asset) && (
          <div className="px-4 py-2 border-t border-gray-100">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Mapper v2 Context</div>
            <div className="space-y-2">
              {infra.land_category && (
                <div className="text-xs text-gray-700">
                  <span className="font-semibold text-gray-900">Land category:</span>{' '}
                  <span className="capitalize">{infra.land_category}</span>
                </div>
              )}
              {infra.asset_mapping && (
                <div className="text-xs text-gray-700">
                  <span className="font-semibold text-gray-900">Asset sync:</span>{' '}
                  {String((infra.asset_mapping as Record<string, unknown>).asset_kind || 'mapped_asset')} via{' '}
                  {String((infra.asset_mapping as Record<string, unknown>).sync_mode || 'materialize_on_accept')}
                </div>
              )}
              {twinState?.operational_asset && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800">
                  Asset-backed record is present in twin state and will stay linked through Mapper materialization.
                </div>
              )}
              {twinState?.parcel_twin && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">Devices:</span>{' '}
                    {twinState.parcel_twin.telemetry?.linked_device_count ?? 0}
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">Weather feeds:</span>{' '}
                    {twinState.parcel_twin.weather?.linked_feed_count ?? 0}
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">NDVI:</span>{' '}
                    {twinState.parcel_twin.ndvi?.status || 'not_started'}
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">Relationships:</span>{' '}
                    {twinState.parcel_twin.relationships?.length || 0}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={syncInfra.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncInfra.isPending ? 'animate-spin' : ''}`} />
                  {twinState?.operational_asset ? 'Sync to Asset App' : 'Create Asset'}
                </button>
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={syncInfra.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-40"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Sync to PIM
                </button>
              </div>
            </div>
          </div>
        )}

        {streamLinks.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Recommended Streams</div>
            <div className="flex flex-wrap gap-1">
              {streamLinks.map((stream) => (
                <span
                  key={stream.stream_id}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100"
                  title={stream.description || stream.stream_id}
                >
                  {stream.stream_id}
                </span>
              ))}
            </div>
          </div>
        )}

        {!editing && infra.attributes && Object.keys(infra.attributes).length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Attributes</div>
            <div className="space-y-1 max-h-28 overflow-y-auto">
              {Object.entries(infra.attributes).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-400 font-mono truncate w-28">{key.replace(/_/g, ' ')}</span>
                  <span className="text-gray-700 truncate">{String(val ?? '-')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
              Owner
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEntitySearchOpen((open) => !open)}
                className="text-[10px] text-blue-600 hover:text-blue-700 font-medium"
              >
                {linkedEntity || infra.linked_entity_id ? 'Change' : 'Link'}
              </button>
              {(linkedEntity || infra.linked_entity_id) && (
                <button
                  onClick={handleClearOwner}
                  className="text-[10px] text-gray-400 hover:text-gray-600 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {linkedEntity || infra.linked_entity_id ? (
            <div className="flex items-center gap-2 text-xs bg-blue-50 rounded-md px-2 py-1.5">
              <Building2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="text-blue-700 font-medium truncate flex-1">
                {linkedEntity?.name || infra.linked_entity_id}
              </span>
              {linkedEntity?.entity_type && (
                <span className="text-[10px] text-blue-500">
                  {linkedEntity.entity_type.replace('legal:', '')}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-300 italic">No owner linked</p>
          )}
          <AnimatePresence>
            {entitySearchOpen && (
              <EntitySearchPicker
                onClose={() => setEntitySearchOpen(false)}
                onLink={(entityId, entityName) => {
                  linkEntity.mutate(
                    { infraId: infra.id, entity_id: entityId },
                    {
                      onSuccess: () => {
                        setEntitySearchOpen(false);
                        toast.success(`${entityName} linked`);
                      },
                      onError: (err) => toast.error('Entity link failed', { description: String(err) }),
                    }
                  );
                }}
              />
            )}
          </AnimatePresence>
        </div>

        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
              API Endpoints ({apiLinks.length})
            </div>
            <button
              onClick={() => setApiSearchOpen((open) => !open)}
              className="text-[10px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
            >
              <Plus className="w-3 h-3" /> Link
            </button>
          </div>
          <AnimatePresence>
            {apiSearchOpen && (
              <ApiEndpointSearchPicker
                linkedEndpoints={apiLinks}
                onClose={() => setApiSearchOpen(false)}
                onLink={(link) => {
                  linkApi.mutate(
                    { infraId: infra.id, link },
                    {
                      onSuccess: () => {
                        setApiSearchOpen(false);
                        toast.success(`Linked ${link.api_name || link.api_id} / ${link.endpoint_id}`);
                      },
                      onError: (err) => toast.error('API link failed', { description: String(err) }),
                    }
                  );
                }}
              />
            )}
          </AnimatePresence>
          {apiLinks.length === 0 ? (
            <p className="text-xs text-gray-300 italic">No API endpoints linked</p>
          ) : (
            <div className="space-y-1">
              {apiLinks.map((link) => {
                const key = `${link.api_id}:${link.endpoint_id}`;
                const endpointDeviceId = link.device_id || `extapi:${link.api_id}:${link.endpoint_id}`;
                return (
                  <div key={key} className="flex items-start gap-2 text-xs bg-gray-50 rounded-md px-2 py-1.5">
                    <Link2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-700 font-medium truncate block">{link.api_name || link.api_id}</span>
                      <span className="text-[10px] text-gray-400 block truncate">
                        {link.method || 'GET'} {link.path || link.endpoint_id}
                      </span>
                      <span className="text-[9px] text-gray-300 block font-mono truncate">{endpointDeviceId}</span>
                    </div>
                    <button
                      onClick={() => {
                        unlinkApi.mutate(
                          { infraId: infra.id, api_id: link.api_id, endpoint_id: link.endpoint_id },
                          {
                            onSuccess: () => toast.info(`Unlinked ${link.endpoint_id}`),
                            onError: (err) => toast.error('API unlink failed', { description: String(err) }),
                          }
                        );
                      }}
                      className="p-0.5 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Unlink className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
              Devices ({infra.linked_device_ids.length})
            </div>
            <div className="flex items-center gap-1">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setDeviceSearchOpen((open) => !open)}
                className="text-[10px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
              >
                <Plus className="w-3 h-3" /> Link
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setDevicePaletteOpen(true)}
                className="text-[10px] text-gray-400 hover:text-gray-600 font-medium"
              >
                Palette
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {deviceSearchOpen && (
              <DeviceSearchPicker
                linkedIds={infra.linked_device_ids}
                onLink={(deviceId) => {
                  linkDevice.mutate(
                    { infraId: infra.id, device_id: deviceId },
                    {
                      onSuccess: () => {
                        setDeviceSearchOpen(false);
                        toast.success(`Device ${deviceId} linked`);
                      },
                      onError: (err) => toast.error('Link failed', { description: String(err) }),
                    }
                  );
                }}
                onClose={() => setDeviceSearchOpen(false)}
              />
            )}
          </AnimatePresence>

          {infra.linked_device_ids.length === 0 ? (
            <p className="text-xs text-gray-300 italic">No devices linked</p>
          ) : (
            <div className="space-y-1">
              {infra.linked_device_ids.map((dId) => {
                const twin = twinState?.devices?.find((device) => device.device.deviceId === dId);
                const isOnline = twin?.health?.status === 'online' || twin?.device?.status === 'active';
                const lastHeartbeat = twin?.health?.lastHeartbeatAt;
                return (
                  <div key={dId} className="flex items-center gap-2 text-xs bg-gray-50 rounded-md px-2 py-1.5">
                    <Cpu className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-700 font-mono truncate block">{dId}</span>
                      {lastHeartbeat && (
                        <span className="text-[9px] text-gray-400 block">
                          Last seen: {new Date(lastHeartbeat).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-red-400'}`} title={isOnline ? 'Online' : 'Offline'} />
                    <button
                      onClick={() => handleUnlinkDevice(dId)}
                      className="p-0.5 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Unlink className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-3 flex items-center gap-2 border-t border-gray-100 sticky bottom-0 bg-white/95 backdrop-blur-md">
          {editing ? (
            <>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveDetails}
                disabled={updateInfra.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
                Save Changes
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleCancelEditing}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </motion.button>
            </>
          ) : isGeometryEditing ? (
            <>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSaveGeometryEdit}
                disabled={updateInfra.isPending || !editingInfrastructureGeometryDraft}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-40 transition-colors"
              >
                <Save className="w-3.5 h-3.5" /> Save Geometry
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleCancelGeometryEdit}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </motion.button>
            </>
          ) : (
            <>
              {infra.status === 'review' && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleConfirmReview}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> Confirm
                </motion.button>
              )}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleStartGeometryEdit}
                disabled={!supportsVisualGeometryEdit}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-violet-600 hover:bg-violet-50 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                title={supportsVisualGeometryEdit ? 'Edit on map' : 'Visual editing currently supports point, line, and polygon geometries'}
              >
                <PenTool className="w-3.5 h-3.5" /> Edit Geometry
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleStartEditing}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSync}
                disabled={syncInfra.isPending}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-blue-600 hover:bg-blue-50 disabled:opacity-40 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncInfra.isPending ? 'animate-spin' : ''}`} /> Sync
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
